import { canonicalFontFamily } from 'admin/components/common/AI/Chat/fonts/font-resolver';

export type FontFamilySelectOption = {
	value: string;
	label: string;
	unavailable?: true;
	disabled?: boolean;
	isDisabled?: boolean;
};

export type FontFamilySelectOptionGroup = {
	label: string;
	options: FontFamilySelectOption[];
};

function uniqueOptionsByFamily(options: FontFamilySelectOption[]) {
	const byFamily = new Map<string, FontFamilySelectOption>();
	options.forEach(option => {
		const family = canonicalFontFamily(option.value);
		if (family && !byFamily.has(family)) byFamily.set(family, option);
	});
	return byFamily;
}

export function isFontFamilyAvailable(value: string, options: FontFamilySelectOption[]): boolean {
	return uniqueOptionsByFamily(options).has(canonicalFontFamily(value));
}

export function getUnavailableFontFamilies(values: string[], options: FontFamilySelectOption[]): string[] {
	const available = uniqueOptionsByFamily(options);
	const unavailable = new Map<string, string>();

	values.forEach(value => {
		const family = canonicalFontFamily(value);
		if (family && !available.has(family) && !unavailable.has(family)) unavailable.set(family, value.trim());
	});

	return [...unavailable.values()];
}

export function buildFontFamilySelectOptions(value: string, options: FontFamilySelectOption[]) {
	const byFamily = uniqueOptionsByFamily(options);
	const uniqueOptions = [...byFamily.values()];
	const availableOption = byFamily.get(canonicalFontFamily(value));
	if (!value || availableOption)
		return { value: availableOption?.value ?? value, unavailable: false, options: uniqueOptions };

	return {
		value,
		unavailable: true,
		options: [
			{
				label: 'Unavailable',
				options: [{ value, label: value, unavailable: true as const, disabled: true, isDisabled: true }],
			},
			{ label: 'Available fonts', options: uniqueOptions },
		] satisfies FontFamilySelectOptionGroup[],
	};
}
